import {Action, Card, DataStatus, DropdownActionMenuItem, openDialog} from 'platform/components';
import {Scroll, VStack} from 'platform/foundation';

import {useLayoutEffect, useRef} from 'react';

import {isEmpty} from 'ramda';

import {useListCustomerMessagesQuery, useSmsGetKonzultaCredentialsQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getCustomerPhoneNumber, useEmailConfig} from '@dms/shared';
import {SendCustomerEmailForm, SendCustomerSmsForm} from '@dms/shared-aftersales';

import {buildArray, parseDate} from 'shared/web';

import {CustomerMessageBubble} from './CustomerMessageBubble';

interface CustomerMessageHistoryProps {
  customerId: string;
}

export function CustomerMessageHistory(props: CustomerMessageHistoryProps) {
  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    isFetching: isFetchingMessages,
    isError: isMessagesError,
    refetch: refetchMessages,
  } = useListCustomerMessagesQuery({customerId: props.customerId});
  const messages = [...(messagesData?.messages ?? [])].sort(
    (a, b) => parseDate(a.createdAt).getTime() - parseDate(b.createdAt).getTime()
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const {data: customer} = useGetCustomerV2Query({customerId: props.customerId});
  const {data: konzultaConfig} = useSmsGetKonzultaCredentialsQuery();
  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();

  const whatsAppNumber = getWhatsAppPhoneNumber(getCustomerPhoneNumber(customer));

  const handleSendEmail = () =>
    openDialog(
      <SendCustomerEmailForm
        customerId={props.customerId}
        recordId={props.customerId}
        resourceId={EntityResourceIds.customer}
      />,
      {scrollBehavior: 'outside', size: 'large', title: i18n.t('general.actions.sendEmail')}
    );

  const handleSendSms = () =>
    openDialog(
      <SendCustomerSmsForm
        customerId={props.customerId}
        recordId={props.customerId}
        resourceId={EntityResourceIds.customer}
      />,
      {scrollBehavior: 'outside', size: 'large', title: i18n.t('general.labels.smsMessage')}
    );

  const handleOpenWhatsApp = () => {
    if (!whatsAppNumber) {
      return;
    }
    window.open(
      `https://wa.me/${whatsAppNumber}?utm_source=omnetic&utm_medium=crm&utm_campaign=whatsapp_chat&crm_contact_id=${props.customerId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const menuItems = buildArray<DropdownActionMenuItem>()
    .add({
      label: i18n.t('general.labels.email'),
      leftIcon: 'communication/email',
      isDisabled: !isEmailConfigEnabled,
      tooltip: !isEmailConfigEnabled
        ? {label: i18n.t('general.notifications.setupEmailProvider'), placement: 'top-start'}
        : undefined,
      onClick: handleSendEmail,
    })
    .add({
      label: i18n.t('general.labels.sms'),
      leftIcon: 'communication/textsms',
      isDisabled: !konzultaConfig?.enabled,
      tooltip: !konzultaConfig?.enabled
        ? {label: i18n.t('general.notifications.setupSmsProvider'), placement: 'top-start'}
        : undefined,
      onClick: handleSendSms,
    })
    .add({
      label: i18n.t('general.labels.whatsapp'),
      leftIcon: 'communication/comment',
      isDisabled: !whatsAppNumber,
      tooltip: !whatsAppNumber
        ? {
            label: i18n.t('page.customer.notifications.noWhatsAppNumber'),
            placement: 'top-start',
          }
        : undefined,
      onClick: handleOpenWhatsApp,
    });

  const actions: Action[] = [
    {
      type: 'iconButton',
      icon: 'navigation/refresh',
      'aria-label': i18n.t('general.actions.refresh'),
      isDisabled: isFetchingMessages,
      onClick: () => {
        refetchMessages();
      },
    },
    {
      type: 'dropdown-button',
      variant: 'secondary',
      rightIcon: 'hardware/keyboard_arrow_down',
      title: i18n.t('general.actions.sendMessage'),
      menuItems,
    },
  ];

  return (
    <Card title={i18n.t('page.customer.labels.chat')} actions={actions}>
      <DataStatus
        isLoading={isLoadingMessages}
        isError={isMessagesError}
        isEmpty={isEmpty(messages)}
        minHeight={40}
      >
        <Scroll ref={scrollRef} auto maxHeight={150}>
          <VStack spacing={4}>
            {messages.map((message) => (
              <CustomerMessageBubble key={message.id} message={message} />
            ))}
          </VStack>
        </Scroll>
      </DataStatus>
    </Card>
  );
}

function getWhatsAppPhoneNumber(phoneNumber: string | null | undefined): string | null {
  if (!phoneNumber?.startsWith('+')) {
    return null;
  }
  return phoneNumber.replace(/\s+/g, '').replace('+', '');
}
